import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ToastOptions, useToast } from '../../../../lib/chakraUi/hooks';
import {
  AnyDocument,
  DocumentTransitionsResult,
  Nullable,
} from '../../../../types';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { getApiErrorMessage } from '../../../logic';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { ApiErrorResponse } from '../../../types';
import { NonPaginatedResponse } from '../../../types/NonPaginatedResponse';
import { getDocumentTransitionsQueryKey } from '../../logic/getDocumentTransitionQueryKey';
import { updateDocumentTransition } from '../../requests/updateDocumentTransition';
import { DocumentParams } from '../../types/DocumentParams';

export const useDocumentTransitionUpdate = <
  DocumentType extends AnyDocument = AnyDocument,
  Payload = Record<string, unknown>,
>(
  documentParams: DocumentParams,
  documentId: DocumentType['id'],
  successMessage: Nullable<ToastOptions> = {
    title: 'Success',
    description: 'Update successful',
  },
) => {
  const { constructPost } = useRequestConstructor();
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = getDocumentTransitionsQueryKey<DocumentType>(
    documentId,
    documentParams,
  );

  const { data, mutateAsync, ...rest } = useMutation<
    Awaited<ReturnType<typeof updateDocumentTransition<Payload>>>,
    ApiErrorResponse,
    Payload
  >({
    mutationKey: queryKey,
    mutationFn: async (payload) =>
      updateDocumentTransition<Payload>(
        constructPost,
        documentParams,
        documentId,
        payload,
      ),
    onError: (error) => {
      toast({
        title: 'Error!',
        description: getApiErrorMessage(error, 'Transition update failed'),
        type: 'error',
      });
    },
    onSuccess: (response) => {
      const transitions = response?.data;
      if (!transitions) return;
      queryClient.setQueryData<NonPaginatedResponse<DocumentTransitionsResult>>(
        queryKey,
        { data: transitions },
      );
      if (successMessage) {
        toast({
          ...successMessage,
          type: 'success',
        });
      }
    },
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(data, 'transitions'),
    ...rest,
    updateDocumentTransition: mutateAsync,
  };
};
